import { Platform } from 'react-native';
import { Redirect, Stack, usePathname, useRouter } from 'expo-router';
import LeagueLayoutStatus from '@/components/navigation/LeagueLayoutStatus';
import {
  getVisibleLeagueTabs,
  hiddenLeagueTabFallback,
  isLeagueTabName,
  isLeagueTabVisible,
  usesNativeLeagueTabs,
  type LeagueTabVisibilityInput,
} from '@/components/navigation/leagueTabVisibility';
import AppHeader from '@/components/AppHeader';
import { useLeague } from '@/hooks/useLeague';
import { LeagueProvider } from '@/contexts/LeagueContext';
import { getLandingHref, getLeagueHref } from '@/lib/routes';
import { clientLogger } from '@/lib/datadog';

export default function LeagueLayout() {
  const router = useRouter();
  const pathname = usePathname();
  const leagueData = useLeague();
  const requestedSection = pathname.split('/').filter(Boolean).at(-1);
  const isLeagueTabRoute = isLeagueTabName(requestedSection);
  // An explicit tab switch only needs the league details and permissions to
  // build its navigator. Waiting for season weeks and tournament data here
  // makes a league switch look stalled even though those screens load their
  // own content.
  const routeIsLoading = isLeagueTabRoute
    ? leagueData.leagueLoading || leagueData.permissionsLoading
    : leagueData.isLoading;

  if (leagueData.isInvalidId) {
    if (__DEV__) {
      clientLogger.warn('[LeagueLayout] Invalid league ID', { leagueId: leagueData.id });
    }
    return <Redirect href={getLandingHref()} />;
  }

  if (!routeIsLoading && !leagueData.league && leagueData.id) {
    return (
      <>
        <AppHeader title="Error" allowBack={true} />
        <LeagueLayoutStatus state="not-found" onReturnHome={() => router.replace(getLandingHref())} />
      </>
    );
  }

  if (routeIsLoading || (leagueData.id && !leagueData.league)) {
    return (
      <>
        <AppHeader title="Loading..." allowBack={false} />
        <LeagueLayoutStatus state="loading" />
      </>
    );
  }

  if (__DEV__) {
    clientLogger.debug('[LeagueLayout] Context', {
      hasLeague: Boolean(leagueData.league),
      isMarchMadness: leagueData.isMarchMadness,
      leagueId: leagueData.id,
    });
  }

  const permissionsReady = !leagueData.permissionsLoading || Boolean(leagueData.permissions);
  const visibility: LeagueTabVisibilityInput = {
    isMarchMadness: leagueData.isMarchMadness,
    showsTournamentBracket: leagueData.isMarchMadness || leagueData.isWorldCupTournament,
    isChatEnabled: leagueData.league?.chatEnabled === true,
    showSettingsTab: Boolean(leagueData.permissions?.isOwner || leagueData.permissions?.isManager),
  };
  const nativeTabs = usesNativeLeagueTabs(Platform.OS, getVisibleLeagueTabs(visibility).length);
  // A hidden native tab is removed from the navigator, so a direct link to it
  // cannot render its own redirect. Send it to the section this league uses.
  if (
    nativeTabs &&
    permissionsReady &&
    isLeagueTabName(requestedSection) &&
    !isLeagueTabVisible(requestedSection, visibility)
  ) {
    return (
      <Redirect
        href={getLeagueHref(leagueData.id, hiddenLeagueTabFallback(requestedSection, visibility), {
          seasonId: leagueData.resolvedSeasonId,
        })}
      />
    );
  }

  return (
    <LeagueProvider value={leagueData}>
      <Stack screenOptions={{ headerShown: false, animation: 'slide_from_right' }} />
    </LeagueProvider>
  );
}
